import type { GetServerSidePropsContext, NextPage } from "next";
import { Suspense, useEffect, useMemo } from "react";
import { SWRConfig } from "swr";
import { NoSsr } from "@mui/material";
import dynamic from "next/dynamic";
import { useRouter } from "next/router";
import { datadogRum } from "@datadog/browser-rum";
import Head from "next/head";
import { getLocale } from "../src/helpers/locale";
import { setAnalyticsPageData } from "../src/dataAccess/storages/analytics";
import {
  DOWNTIME_STATUS_CODE,
  LOGGED_IN_COOKIE_NAME,
  LOGGED_IN_COOKIE_VALUE,
  SSO_REDIRECT_PARAM,
} from "../src/dataAccess/constants";
import { localStorageWrapper } from "../src/dataAccess/LocalStorage";
import { IntercomWrapper } from "../src/app/IntercomWrapper";
import { ServerError } from "../src/dataAccess/ServerError";
import { canAccessPage } from "../src/app/Authorization";
import {
  getRouteFromServerSideContext,
  pageMapping,
  useCurrentRoute,
} from "../src/builder/mapping/page";
import {
  Configuration,
  communitiesFallbackKey,
  getConfiguration,
} from "../src/dataAccess/api/configuration";
import { SSR, validateHost } from "../src/helpers/environment";
import { adminKey, useEditor } from "../src/wysiwyg/EditorCtx";
import { App, getPageConfiguration } from "../src/app/App";
import { GlobalStyles } from "../src/app/GlobalStyles";
import Maintenance, { isMaintenanceOn } from "../src/app/Maintenance";
import { UserProvider } from "../src/app/UserProviderCtx";
import { forceIncludeComponents } from "../src/app/componentHooks";
import { useSSOPopupListener } from "../src/app/ssoPopup";
import PublishedTemplateNotFound from "../src/components/ui/SiteMessage/PublishedTemplateNotFound";
import MarketplaceNotFound from "../src/components/ui/SiteMessage/MarketplaceNotFound";
import { parseHTMLTags } from "../src/helpers/html";
import { PageStatusProvider } from "../src/app/PageStatusCtx";
import { addCspHeaders } from "../src/helpers/policies";
import { ApiClientProvider, RequestData } from "../src/app/ApiClientCtx";
import { HTTPClientV3 } from "../src/dataAccess/HTTPClientV3";

datadogRum.init({
  applicationId: "fc441633-0c45-4621-83e3-8ef7c34d8bde",
  clientToken: "pubedf97b73e78ac41c98893584336325c5",
  // `site` refers to the Datadog site parameter of your organization
  // see https://docs.datadoghq.com/getting_started/site/
  site: "us3.datadoghq.com",
  service: "builtfirst-buyer-frontend",
  env: process.env.NEXT_PUBLIC_API_URL_V3 || "local",
  // Specify a version number to identify the deployed version of your application in Datadog
  // version: '1.0.0',
  sessionSampleRate: 100,
  sessionReplaySampleRate: 20,
  defaultPrivacyLevel: "mask-user-input",
  sessionPersistence: "local-storage",
});

const EditorAuthorization = dynamic(
  () => import("../src/app/EditorAuthorization"),
);

export interface Fallback {
  [key: string]: {
    data: Configuration;
    meta: any;
  };
}

function getRedirectForLanguage(
  context: GetServerSidePropsContext,
  supportedLocales: string[],
) {
  if (!supportedLocales?.length) {
    return null;
  }

  const acceptLang = context.req.headers["accept-language"] || "";
  const matchedLocale =
    acceptLang
      .split(",")
      .map((lang) => lang.split(";")[0].split("-")[0].toLowerCase())
      .find((lang) => supportedLocales.includes(lang)) || "";

  const supportedLocale = matchedLocale || "en";

  const currentPath = context.req.url || "";

  const hasLangInPath = !!getLocale(currentPath)?.locale;

  const excludedRoute = /\/(.*\..*|_next).*/.test(currentPath);

  if (
    supportedLocale &&
    supportedLocale !== "en" &&
    !hasLangInPath &&
    !excludedRoute
  ) {
    return {
      redirect: {
        destination: `/${supportedLocale}${currentPath}`,
        permanent: false,
      },
    };
  }

  return null;
}

export const getServerSideProps = async (
  context: GetServerSidePropsContext,
) => {
  const cookies = context.req.headers.cookie || "";
  const { jwt } = context.query;
  const { [SSO_REDIRECT_PARAM]: ssoredirect } = context.query;
  const loggedIn =
    cookies.includes(`${LOGGED_IN_COOKIE_NAME}=${LOGGED_IN_COOKIE_VALUE}`) ||
    !!jwt ||
    !!ssoredirect;
  const nonce = String(context.req.headers["x-nonce"] || "");

  const defaultResponse: {
    props: {
      fallback: null | Fallback;
      SSRRequired: boolean;
      maintenance: boolean;
      pageNotFound: boolean;
      nonce: string;
      requestData: RequestData;
    };
  } = {
    props: {
      fallback: null,
      SSRRequired: false,
      maintenance: false,
      pageNotFound: false,
      nonce,
      requestData: {
        host: null,
        url: null,
        marketplaceNoAccounts: null,
      },
    },
  };

  if (!validateHost(context.req.headers.host)) {
    return defaultResponse;
  }

  const APIV3 = new HTTPClientV3(
    context.req.headers.host || null,
    context.resolvedUrl || null,
    null,
  );
  const route = getRouteFromServerSideContext(context);
  const { json, fallbackKey } = getConfiguration(APIV3);
  return json()
    .then(async (configuration) => {
      const {
        data: {
          publishedTemplate: theme,
          isOpenMarketplace,
          embedUrls,
          communityToggleSetting,
          limited,
        },
      } = configuration;

      addCspHeaders({
        context,
        embedUrls,
        communityToggleSetting,
        hideSupportChat: theme?.properties?.settings?.hideSupportChat,
        nonce,
      });

      configuration.data = forceIncludeComponents(configuration.data);

      const fallbackConfiguration = { [fallbackKey]: configuration };
      const pageConfiguration = getPageConfiguration(
        theme?.properties?.pages,
        route,
      );
      const currentPage = route?.id && pageMapping[route?.id];

      const editorMode = context.query[adminKey] !== undefined;
      const preview = context.query["editor-preview"] !== undefined;

      const { publishedTemplate } = configuration.data;
      const currentPath = context.req.url || "";
      const pathLocale = getLocale(currentPath)?.locale;

      const localeExist =
        preview ||
        editorMode ||
        !publishedTemplate?.alternatives ||
        !pathLocale ||
        !!publishedTemplate.alternatives.find(
          ({ locale }) => locale === pathLocale,
        );

      const pageNotFound = !currentPage || !localeExist;
      if (pageNotFound) {
        context.res.statusCode = 404;
      }

      const languageRedirection =
        route?.id && !editorMode
          ? getRedirectForLanguage(
              context,
              (configuration.data.publishedTemplate?.alternatives || [])
                ?.map(({ locale }) => locale || "")
                .filter(Boolean),
            )
          : null;

      if (languageRedirection) {
        return { ...defaultResponse, ...languageRedirection };
      }

      /**
       * Custom domain redirect scenario
       */
      if (
        configuration.data.customDomain &&
        configuration.data.customDomain !== context.req.headers.host
      ) {
        return {
          redirect: {
            destination: `https://${configuration.data.customDomain}`,
            permanent: true,
          },
          ...defaultResponse,
        };
      }

      const minimumSSRRequired = !limited && !editorMode && !loggedIn;

      if (!pageConfiguration) {
        defaultResponse.props.fallback = fallbackConfiguration;
        defaultResponse.props.pageNotFound = pageNotFound;
        defaultResponse.props.SSRRequired = minimumSSRRequired;
        return defaultResponse;
      }

      const SSRRequired =
        canAccessPage(isOpenMarketplace, currentPage?.access) &&
        minimumSSRRequired;

      if (currentPage && SSRRequired && "namedExports" in currentPage) {
        const namedExports = await currentPage?.namedExports();
        if (namedExports && "getServerSideProps" in namedExports) {
          let pageFallback: Fallback = {};
          try {
            const {
              props: { fallback, resourceNotFound },
            } = await namedExports.getServerSideProps(
              context,
              configuration.data,
            );
            pageFallback = fallback;
            if (resourceNotFound) {
              context.res.statusCode = 404;
            }
          } catch (e) {
            console.error(e);
          }

          return {
            props: {
              SSRRequired,
              maintenance: false,
              pageNotFound,
              fallback: {
                ...pageFallback,
                ...fallbackConfiguration,
              },
              nonce,
              requestData: {
                host: context.req.headers.host,
                url: context.resolvedUrl,
                marketplaceNoAccounts:
                  configuration.data.isOpenMarketplace &&
                  !configuration.data.forceAccounts,
              },
            },
          };
        }
      }

      return {
        props: {
          SSRRequired,
          fallback: fallbackConfiguration,
          nonce,
          maintenance: false,
          pageNotFound,
          requestData: {
            host: context.req.headers.host,
            url: context.resolvedUrl,
            marketplaceNoAccounts: null,
          },
        },
      };
    })
    .catch((e) => {
      if (e instanceof ServerError) {
        const code = e.getCode();
        if (code === DOWNTIME_STATUS_CODE) {
          defaultResponse.props.maintenance = true;
        }
        if (code === "community_not_found") {
          context.res.statusCode = 410;
        }
        return defaultResponse;
      }
      throw e;
    });
};

type Props = Awaited<ReturnType<typeof getServerSideProps>>["props"];

const Page: NextPage<Props> = ({
  fallback,
  SSRRequired,
  maintenance,
  pageNotFound,
  nonce,
  requestData,
}: Props) => {
  const route = useCurrentRoute();
  const router = useRouter();
  const { editorValue, editorParamExist, templateId } = useEditor();

  const publishedTemplate = fallback
    ? fallback[communitiesFallbackKey].data.publishedTemplate
    : null;
  const publishedTemplateId = publishedTemplate?.id;

  useEffect(() => {
    setAnalyticsPageData();
  }, [router]);

  useSSOPopupListener();

  const client = useMemo(
    () =>
      new HTTPClientV3(
        requestData.host || null,
        requestData.url || null,
        requestData.marketplaceNoAccounts || null,
      ),
    [requestData],
  );

  if (
    (maintenance ||
      isMaintenanceOn() ||
      process.env.NEXT_PUBLIC_MAINTENANCE === "true") &&
    !localStorageWrapper.getItem("bypass")
  ) {
    return (
      <IntercomWrapper>
        <Maintenance />
      </IntercomWrapper>
    );
  }

  if (!fallback) {
    return <MarketplaceNotFound />;
  }

  if (!publishedTemplate && !editorParamExist) {
    return SSR ? <></> : <PublishedTemplateNotFound />;
  }

  if (SSR && !SSRRequired) {
    return <></>;
  }

  const PageComponent = route?.id && pageMapping[route?.id].component;

  const app = (
    <App
      PageComponent={pageNotFound ? undefined : PageComponent}
      fallback={fallback}
    />
  );

  const { customHeadContent } =
    publishedTemplate?.properties.settings.integrations?.snippets || {};

  return (
    <Suspense>
      <SWRConfig
        value={{
          fallback: fallback || undefined,
          revalidateOnFocus: false,
          revalidateOnReconnect: false,
          // This must be false so that if there is nothing in cache a request is executed
          revalidateIfStale: false,
          // This must be undefined so avoid refeshing the page too many times
          revalidateOnMount: undefined,
          shouldRetryOnError: false,
        }}
      >
        {customHeadContent && (
          <Head>{parseHTMLTags(customHeadContent, nonce)}</Head>
        )}
        <ApiClientProvider client={client}>
          <PageStatusProvider>
            <UserProvider>
              <GlobalStyles />
              {!SSR && editorParamExist ? (
                <NoSsr>
                  <EditorAuthorization
                    template={
                      fallback[communitiesFallbackKey].data.publishedTemplate
                    }
                    isEditor={editorParamExist}
                    adminValue={editorValue || ""}
                    templateId={templateId}
                    publishedTemplateId={publishedTemplateId}
                  >
                    {app}
                  </EditorAuthorization>
                </NoSsr>
              ) : (
                <>{app}</>
              )}
            </UserProvider>
          </PageStatusProvider>
        </ApiClientProvider>
      </SWRConfig>
    </Suspense>
  );
};

export default Page;
